class EEConfiguratorProductInfo extends HTMLElement {
  constructor() {
    super();
    this.variantSelectors = this.querySelector('ee-variant-selects');
    this.productForm = this.querySelector('ee-product-form');
    this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.errorMessageWrapper?.querySelector('.product-form__error-message');
    
    // Track current images for scroll behavior
    this.originalProductImage = null;
    this.configuratorImage = null;
    this.scrollThreshold = 260;
    this.isShowingConfigurator = true;

    // Add properties for pricing calculations
    this.currentVariant = null;
    this.configuratorAddOns = [];

    if (!this.variantSelectors) return;
    
    // Setup event listeners
    this.variantSelectors.addEventListener('ee-variant-change', this.onVariantChange.bind(this));
    
    // Setup pricing event listeners
    this.setupPricingEventListeners();
    
    // Handle URL params if present (e.g. ?variant=123456789)
    this.updateOptionsFromURL();

    // Initialize selected card styling
    this.initSelectedCardStyling();

    // Dispatch initial state to any listeners
    this.dispatchVariantChangeEvent(this.getSelectedVariant());

    // Add event listener for configurator card clicks to update main image
    this.setupConfiguratorCardImageUpdate();
    
    // Setup scroll-based image swapping
    this.setupScrollImageSwap();
  }

  // Setup event listeners for pricing calculations
  setupPricingEventListeners() {
    // Listen for add-on selection changes
    document.addEventListener('addon-selection-changed', () => {
      // Ensure default/selected variant is resolved even before any manual variant click
      if (!this.currentVariant) {
        this.currentVariant = this.getSelectedVariant();
      }
      this.updateConfiguratorAddOns();
      this.calcConfiguratorPrice();
      this.configBreadFinancing();
    });

    // Listen for configurator variant changes
    this.addEventListener('configurator:variant-changed', (event) => {
      this.currentVariant = event.detail.variant;
      this.configBreadFinancing();
      this.calcConfiguratorPrice();
    });

    // Initialize after a short delay to ensure all components are loaded
    setTimeout(() => {
      this.currentVariant = this.getSelectedVariant();
      this.updateConfiguratorAddOns();
      this.calcConfiguratorPrice();
      this.configBreadFinancing();
      if (this.currentVariant) {
        this.updateProductMediaGallery(this.currentVariant);
      }
    }, 100);
  }

  // Update the configuratorAddOns array with current selections
  updateConfiguratorAddOns() {
    const configuratorAddOnsComponent = document.querySelector('configurator-add-ons');
    if (!configuratorAddOnsComponent) {
      this.configuratorAddOns = [];
      return;
    }

    // Get all add-on inputs
    this.configuratorAddOns = Array.from(document.querySelectorAll('configurator-add-ons input[type="checkbox"], configurator-add-ons input[type="radio"]')).map(input => ({
      checked: input.checked,
      dataset: {
        price: input.dataset.price || 0,
        comparePrice: input.dataset.comparePrice || input.dataset.price || 0
      }
    }));
  }

  configBreadFinancing(packagePrice, packageCompare, ShopCurrency) {
    if (packagePrice == null) return;
    const placement = [
      {
        domID: "bread-placement-configurator",
        allowCheckout: false,
        order: {
          items: [],
          subTotal: {
            value: packageCompare,
            currency: ShopCurrency,
          },
          totalTax: {
            value: 0,
            currency: ShopCurrency,
          },
          totalShipping: {
            value: 0,
            currency: ShopCurrency,
          },
          totalDiscounts: {
            value: packageCompare - packagePrice,
            currency: ShopCurrency,
          },
          totalPrice: {
            value: packagePrice,
            currency: ShopCurrency,
          },
        },
      },
    ];
    BreadPayments.init();
    BreadPayments.registerPlacements(placement);
    BreadPayments.on("INSTALLMENT:APPLICATION_CHECKOUT", () => {});
    BreadPayments.on("INSTALLMENT:APPLICATION_DECISIONED", () => {});
  }
  // Main pricing calculation function
  calcConfiguratorPrice() {
    if (!this.currentVariant) {
      this.currentVariant = this.getSelectedVariant();
    }
    if (!this.currentVariant) return;

    let packagePrice = Number(this.currentVariant.price) || 0;
    let packageCompare =
      Number(this.currentVariant.compare_at_price) || packagePrice;

    this.configuratorAddOns.forEach((addOn) => {
      if (addOn.checked) {
        packagePrice += parseInt(addOn.dataset.price);
        packageCompare += parseInt(addOn.dataset.comparePrice) || parseInt(addOn.dataset.price);
      }
    });

    const price = document.querySelector(`.js-price`);
    const salePrice = document.querySelector(`.js-comparePrice`);
    const savings = document.querySelector(`.js-savings`);
    
    if (!price) return;

    price.innerHTML = this.formatCurrency(
      packagePrice,
      Shopify.currency.active,
      Shopify.currency.rate,
      "money"
    );

    if (packageCompare > packagePrice) {
      salePrice.innerHTML = this.formatCurrency(
        packageCompare,
        Shopify.currency.active,
        Shopify.currency.rate,
        "money"
      );
      let savingsAmount = packageCompare - packagePrice;
      savings.innerHTML = this.formatCurrency(
        savingsAmount,
        Shopify.currency.active,
        Shopify.currency.rate,
        "money"
      );
      // Show the sale price element
      salePrice.classList.remove('hidden');
    } else {
      salePrice.innerHTML = "";
      savings.innerHTML = "$0.00";
      // Hide the sale price element
      salePrice.classList.add('hidden');
    }

    this.toggleAddButton(
      !this.currentVariant.available,
      window.variantStrings?.soldOut || 'Sold out'
    );
    this.calcBreadPrice(packagePrice);
    this.configBreadFinancing(
      packagePrice,
      packageCompare,
      Shopify.currency.active
    );
  }

  // Format currency helper function
  formatCurrency(amount, currency, rate, format) {
    // Convert from cents to dollars for display
    const dollars = amount / 100;
    
    // Apply currency rate if different currency
    const convertedAmount = dollars * (rate || 1);
    
    // Format based on Shopify's money format
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return formatter.format(convertedAmount);
  }

  // Calculate bread financing price
  calcBreadPrice(packagePrice) {
    const breadPriceElement = document.querySelector('#js-bread-price');
    if (!breadPriceElement) return;
    
    // Convert from cents to dollars
    const priceInDollars = packagePrice / 100;
    
    // Calculate monthly payment by dividing by 39.44 (matches live site behavior)
    const monthlyPayment = priceInDollars / 39.44;
    
    breadPriceElement.textContent = this.formatCurrency(
      monthlyPayment * 100, // Convert back to cents for formatting
      Shopify.currency.active,
      Shopify.currency.rate,
      "money"
    );
  }
  // Toggle add to cart button state.
  // Only update the label <span> — never button.textContent, which would wipe the loading spinner.
  toggleAddButton(disabled, text) {
    const addButton = this.querySelector(
      'ee-product-form [name="add"], .product-form__submit[name="add"], .product-form__cart-submit'
    );
    if (!addButton) return;

    const label =
      addButton.querySelector('span:not(.sold-out-message):not(.loading__spinner)') ||
      addButton.querySelector(':scope > span');

    if (!addButton.dataset.originalText) {
      addButton.dataset.originalText = (
        label?.textContent ||
        window.variantStrings?.addToCart ||
        'Add to cart'
      ).trim();
    }

    if (disabled) {
      addButton.setAttribute('disabled', 'disabled');
      addButton.classList.add('disabled');
      if (label) label.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButton.classList.remove('disabled');
      if (label) label.textContent = addButton.dataset.originalText;
    }
  }

  // Setup scroll event handler for image swapping based on scroll position
  setupScrollImageSwap() {
    if (document.querySelector(`[id^="VariantOverlay-"]`)) {
      return;
    }
    // debounce function to limit scroll event processing
    const debounce = (func, wait) => {
      let timeout;
      return function() {
        const context = this;
        const args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
      };
    };
    
    // Handler for scroll events
    const handleScroll = () => {
      const scrollPosition = window.scrollY || window.pageYOffset;
      
      if (scrollPosition > this.scrollThreshold && this.isShowingConfigurator) {
        // Switch to original product image when scrolling down past threshold
        this.swapToOriginalImage();
        this.isShowingConfigurator = false;
      } else if (scrollPosition <= this.scrollThreshold && !this.isShowingConfigurator) {
        // Switch back to configurator image when scrolling up past threshold
        this.swapToConfiguratorImage();
        this.isShowingConfigurator = true;
      }
    };
    
    // Register scroll event with debounce for performance
    window.addEventListener('scroll', debounce(handleScroll, 50));
    
    // Also handle initial page load state
    setTimeout(handleScroll, 100);
  }
  
  // Initialize event listeners for configurator card image updates
  setupConfiguratorCardImageUpdate() {
    if (document.querySelector(`[id^="VariantOverlay-"]`)) {
      return;
    }
    const configuratorCards = this.querySelectorAll('.config-card-container input');
    configuratorCards.forEach(input => {
      input.addEventListener('change', (event) => {
        if (event.target.type === 'checkbox' || event.target.name.startsWith('addon-')) {
          return;
        }
        
        if (event.target.checked) {
          const cardContainer = event.target.closest('.config-card-container');
          if (cardContainer) {
            const cardImage = cardContainer.querySelector('img');
            if (cardImage && cardImage.src) {
              this.updateMainProductImage(cardImage.src);
            }
          }
        }
      });
    });
  }

  usesOverlayGallery() {
    return !!document.getElementById(`VariantOverlay-${this.dataset.section}`);
  }
  
  // Switch to the original product image
  swapToOriginalImage() {
    if (!this.originalProductImage) return;
    
    const mainImageContainer = document.querySelector('.product__media-list .product__media-item.is-active');
    if (mainImageContainer) {
      this.updateImageElementsInContainer(mainImageContainer, this.originalProductImage);
    }
    
    // Also update modal images
    document.querySelectorAll('.product-media-modal__content img').forEach(img => {
      img.src = this.originalProductImage;
      if (img.srcset) {
        img.srcset = this.originalProductImage;
      }
    });
  }
  
  // Switch to the configurator card image
  swapToConfiguratorImage() {
    if (!this.configuratorImage) return;
    
    const mainImageContainer = document.querySelector('.product__media-list .product__media-item.is-active');
    if (mainImageContainer) {
      this.updateImageElementsInContainer(mainImageContainer, this.configuratorImage);
    }
    
    // Also update modal images
    document.querySelectorAll('.product-media-modal__content img').forEach(img => {
      img.src = this.configuratorImage;
      if (img.srcset) {
        img.srcset = this.configuratorImage;
      }
    });
  }
  
  // Helper to update image elements within a container
  updateImageElementsInContainer(container, imageSrc) {
    container.querySelectorAll('img').forEach(img => {
      img.src = imageSrc;
      if (img.srcset) {
        img.srcset = imageSrc;
      }
    });
    
    container.querySelectorAll('.product__media').forEach(el => {
      el.style.backgroundImage = `url("${imageSrc}")`;
      
      const nestedImg = el.querySelector('img');
      if (nestedImg) {
        nestedImg.src = imageSrc;
        if (nestedImg.srcset) {
          nestedImg.srcset = imageSrc;
        }
      }
    });
  }

  // Updates the main product image with the provided image source
  updateMainProductImage(imageSrc) {
    if (!imageSrc) return;
    
    // Store configurator image for scroll swapping
    this.configuratorImage = imageSrc;
    
    setTimeout(() => {
      // 1. Try to update the active media item's images
      const mainImageContainer = document.querySelector('.product__media-list .product__media-item.is-active');
      if (mainImageContainer) {
        // Store original image if not already saved
        if (!this.originalProductImage) {
          const img = mainImageContainer.querySelector('img');
          if (img && img.src) {
            this.originalProductImage = img.src;
          }
        }
        
        this.updateImageContainer(mainImageContainer, imageSrc);
      } else {
        // 2. Fallback: Update the first media item if no active item found
        const firstMediaItem = document.querySelector('.product__media-list .product__media-item');
        if (firstMediaItem) {
          // Store original image if not already saved
          if (!this.originalProductImage) {
            const img = firstMediaItem.querySelector('img');
            if (img && img.src) {
              this.originalProductImage = img.src;
            }
          }
          
          this.updateImageContainer(firstMediaItem, imageSrc);
          
          // Make this item active if needed
          if (!firstMediaItem.classList.contains('is-active')) {
            document.querySelectorAll('.product__media-item').forEach(item => {
              item.classList.remove('is-active');
            });
            firstMediaItem.classList.add('is-active');
          }
        } else {
          // 3. Last resort: Update any product image
          if (!this.originalProductImage) {
            const firstProductImage = document.querySelector('.product__media-wrapper img');
            if (firstProductImage && firstProductImage.src) {
              this.originalProductImage = firstProductImage.src;
            }
          }
          
          this.updateAllProductImages(imageSrc);
        }
      }
      
      // Update modal/lightbox images and zoom features
      this.updateSupplementaryImages(imageSrc);
      
      // After updating image, check current scroll position and adjust if needed
      const scrollPosition = window.scrollY || window.pageYOffset;
      this.isShowingConfigurator = scrollPosition <= this.scrollThreshold;
    }, 0);
  }
  
  // Updates all images within a specific container
  updateImageContainer(container, imageSrc) {
    // Update all direct image elements
    container.querySelectorAll('img').forEach(img => {
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.src;
      }
      img.src = imageSrc;
      if (img.srcset) {
        img.srcset = imageSrc;
      }
    });
    
    // Update background images
    container.querySelectorAll('.product__media').forEach(el => {
      el.style.backgroundImage = `url("${imageSrc}")`;
      
      // Check for nested images
      const nestedImg = el.querySelector('img');
      if (nestedImg) {
        nestedImg.src = imageSrc;
        if (nestedImg.srcset) {
          nestedImg.srcset = imageSrc;
        }
      }
    });
  }
  
  // Updates all product images as a last resort
  updateAllProductImages(imageSrc) {
    const productImages = document.querySelectorAll('.product__media-wrapper img');
    productImages.forEach(img => {
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.src;
      }
      img.src = imageSrc;
      if (img.srcset) {
        img.srcset = imageSrc;
      }
    });
  }
  
  // Updates supporting elements like modals and zoom features
  updateSupplementaryImages(imageSrc) {
    // Update modal/lightbox images
    document.querySelectorAll('.product-media-modal__content img').forEach(img => {
      img.src = imageSrc;
      if (img.srcset) {
        img.srcset = imageSrc;
      }
    });
    
    // Update zoom features
    document.querySelectorAll('[data-zoom]').forEach(el => {
      el.dataset.zoom = imageSrc;
    });
    
    // Force browser to repaint
    document.body.offsetHeight;
  }

  initSelectedCardStyling() {
    // Mark initially checked radio inputs' cards as selected
    const checkedInputs = this.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
    checkedInputs.forEach(input => {
      const cardContainer = input.closest('.config-card-container');
      if (cardContainer) {
        cardContainer.classList.add('selected');
        
        // Skip updating main image for add-on inputs
        if (input.type === 'checkbox' || input.name.startsWith('addon-')) {
          console.log('Skipping initial main image update for add-on:', input.name);
          return;
        }
        
        if (!this.usesOverlayGallery()) {
          const cardImage = cardContainer.querySelector('img');
          if (cardImage && cardImage.src) {
            this.updateMainProductImage(cardImage.src);
          }
        }
      }
    });
    
    // If no cards are selected but we have a variant ID in the URL, try to select the corresponding card
    if (checkedInputs.length === 0) {
      const searchParams = new URLSearchParams(window.location.search);
      const variantId = searchParams.get('variant');
      
      if (variantId) {
        // Look for inputs with matching variant IDs
        const variantInputs = this.querySelectorAll(`input[data-variant-id="${variantId}"]`);
        variantInputs.forEach(input => {
          input.checked = true;
          const cardContainer = input.closest('.config-card-container');
          if (cardContainer) {
            // Remove selected class from all sibling cards first
            const fieldset = cardContainer.closest('fieldset');
            if (fieldset) {
              fieldset.querySelectorAll('.config-card-container').forEach(card => {
                card.classList.remove('selected');
              });
            }
            cardContainer.classList.add('selected');
            
            if (!this.usesOverlayGallery()) {
              const cardImage = cardContainer.querySelector('img');
              if (cardImage && cardImage.src) {
                this.updateMainProductImage(cardImage.src);
              }
            }
          }
        });
      }
    }
  }

  onVariantChange(event) {
    // Check if this is a configurator card variant change
    if (event.detail && event.detail.isConfiguratorCard && event.detail.variantId) {
      // Handle configurator card variant change
      const variantId = event.detail.variantId;
      const variants = this.getVariantData();
      
      // Find the variant with this ID
      const selectedVariant = variants.find(variant => variant.id == variantId);
      if (selectedVariant) {
        // Update UI with the selected variant
        this.updateURL(undefined, selectedVariant.id);
        this.updateVariantInput(selectedVariant.id);
        this.updateVariantStatuses(selectedVariant);
        this.dispatchVariantChangeEvent(selectedVariant);

        if (!this.usesOverlayGallery()) {
          const cardInput = event.detail.target;
          if (cardInput && cardInput.closest) {
            const cardContainer = cardInput.closest('.config-card-container');
            if (cardContainer) {
              const cardImage = cardContainer.querySelector('img');
              if (cardImage && cardImage.src) {
                this.updateMainProductImage(cardImage.src);
                this.resetAddOnImages();
              }
            }
          }
        } else {
          try {
            document.dispatchEvent(new Event('configurator:addon-layer-change'));
          } catch (_) {}
        }
      }
      return;
    }
    
    // Standard variant change handling
    this.updateOptions();
    this.updateMasterId();
    
    // Check if this change came from a configurator card
    const target = event.target;
    if (target && target.tagName === 'INPUT' && target.closest('.config-card-container')) {
      // Get the variant ID from the data attribute
      const variantId = target.dataset.variantId;
      if (variantId) {
        // Find the variant with this ID
        const variants = this.getVariantData();
        const selectedVariant = variants.find(variant => variant.id == variantId);
        
        if (selectedVariant) {
          // Update all form inputs with this variant ID
          this.updateVariantInput(selectedVariant.id);
          this.updateURL(undefined, selectedVariant.id);
          this.updateVariantStatuses(selectedVariant);
          this.dispatchVariantChangeEvent(selectedVariant);

          if (!this.usesOverlayGallery()) {
            const cardContainer = target.closest('.config-card-container');
            if (cardContainer) {
              const cardImage = cardContainer.querySelector('img');
              if (cardImage && cardImage.src) {
                this.updateMainProductImage(cardImage.src);
                this.resetAddOnImages();
              }
            }
          } else {
            try {
              document.dispatchEvent(new Event('configurator:addon-layer-change'));
            } catch (_) {}
          }
          return;
        }
      }
    }
    
    // Continue with standard variant selection
    const selectedVariant = this.getSelectedVariant();
    if (selectedVariant) {
      this.updateURL();
      this.updateVariantStatuses();
      this.renderProductInfo();
      this.updateVariantInput();
      
      if (this.usesOverlayGallery()) {
        try {
          document.dispatchEvent(new Event('configurator:addon-layer-change'));
        } catch (_) {}
      } else {
        this.resetAddOnImages();
      }

      // Dispatch event with the selected variant
      this.dispatchVariantChangeEvent(selectedVariant);
    }
  }

  resetAddOnImages() {
    const addOnsEl = document.querySelector('configurator-add-ons');
    const openMode =
      window.EeConfiguratorGallery?.isOpenScrollMode?.() ?? window.scrollY > 200;
    if (addOnsEl?.syncAllSelectedLayers) {
      addOnsEl.syncAllSelectedLayers(openMode);
    }
    if (window.EeConfiguratorGallery?.updateImageVisibility) {
      window.EeConfiguratorGallery.updateImageVisibility(openMode);
    } else {
      try {
        document.dispatchEvent(new Event('configurator:addon-layer-change'));
      } catch (_) {}
    }
  }

  updateOptionsFromURL() {
    const searchParams = new URLSearchParams(window.location.search);
    const variantId = searchParams.get('variant');
    
    if (!variantId) return;
    
    const variant = this.getVariantData().find(variant => variant.id == variantId);
    if (!variant) return;
    
    // Update the selected options based on the URL parameter
    this.updateSelectorsFromVariant(variant);
  }

  updateSelectorsFromVariant(variant) {
    // If we found a matching variant, update the selectors to match its options
    const selects = Array.from(this.querySelectorAll('select'));
    const radios = Array.from(this.querySelectorAll('fieldset input[type="radio"]'));

    variant.options.forEach((option, index) => {
      const select = selects[index];
      if (select) {
        const selectOption = select.querySelector(`option[value="${option}"]`);
        if (selectOption) {
          select.value = option;
          selectOption.setAttribute('selected', 'selected');
        }
      }

      const radio = radios.find(radio => radio.value === option);
      if (radio) {
        radio.checked = true;
        
        // Also update the card styling
        const cardContainer = radio.closest('.config-card-container');
        if (cardContainer) {
          // Remove selected class from all sibling cards
          const fieldset = cardContainer.closest('fieldset');
          if (fieldset) {
            fieldset.querySelectorAll('.config-card-container').forEach(card => {
              card.classList.remove('selected');
            });
          }
          
          // Add selected class to this card
          cardContainer.classList.add('selected');
        }
      }
    });
  }

  updateOptions() {
    // For future option-specific updates
  }

  updateMasterId() {
    // Update the master selector ID if needed
  }

  updateURL(url, variantId) {
    if (!url) {
      url = window.location.pathname;
    }
    
    const searchParams = new URLSearchParams(window.location.search);
    
    if (!variantId) {
      const selectedVariant = this.getSelectedVariant();
      if (!selectedVariant) return;
      variantId = selectedVariant.id;
    }
    
    searchParams.set('variant', variantId);
    window.history.replaceState(null, '', `${url}?${searchParams.toString()}`);
  }

  updateVariantStatuses(providedVariant) {
    const selectedVariant = providedVariant || this.getSelectedVariant();
    if (!selectedVariant) return;
    
    // Update any status displays
    const priceElement = document.getElementById(`ee-price-${this.dataset.section}`);
    if (priceElement) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      });
      
      const price = formatter.format(selectedVariant.price / 100);
      const comparePrice = selectedVariant.compare_at_price ? 
        formatter.format(selectedVariant.compare_at_price / 100) : null;
      
      if (comparePrice && comparePrice > price) {
        priceElement.innerHTML = `<s>${comparePrice}</s> ${price}`;
      } else {
        priceElement.innerHTML = price;
      }
    }
    
    // Update SKU information
    const skuElement = document.getElementById(`EESku-${this.dataset.section}`);
    if (skuElement && selectedVariant) {
      skuElement.textContent = selectedVariant.sku || '';
    }
    
    // Update inventory information
    this.updateInventoryStatus(selectedVariant);
  }

  updateInventoryStatus(selectedVariant) {
    const inventoryElement = document.getElementById(`EEInventory-${this.dataset.section}`);
    if (!inventoryElement) return;
    
    if (!selectedVariant) {
      inventoryElement.classList.add('visibility-hidden');
      return;
    }
    
    if (selectedVariant.inventory_management !== 'shopify') {
      inventoryElement.classList.add('visibility-hidden');
      return;
    }
    
    inventoryElement.classList.remove('visibility-hidden');
    
    // You can customize the inventory display logic here
    // This is just a basic example
    if (selectedVariant.inventory_quantity <= 0) {
      if (selectedVariant.inventory_policy === 'continue') {
        inventoryElement.innerHTML = `
          <span class="svg-wrapper" style="color: rgb(62, 214, 96)">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          ${window.translations.outOfStockContinueSelling || 'Out of stock - backorders allowed'}
        `;
      } else {
        inventoryElement.innerHTML = `
          <span class="svg-wrapper" style="color: rgb(200, 200, 200)">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          ${window.translations.outOfStock || 'Out of stock'}
        `;
      }
    } else if (selectedVariant.inventory_quantity <= 10) {
      inventoryElement.innerHTML = `
        <span class="svg-wrapper" style="color: rgb(238, 148, 65)">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        ${window.translations.lowStock || 'Low stock'} (${selectedVariant.inventory_quantity})
      `;
    } else {
      inventoryElement.innerHTML = `
        <span class="svg-wrapper" style="color: rgb(62, 214, 96)">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        ${window.translations.inStock || 'In stock'} (${selectedVariant.inventory_quantity})
      `;
    }
  }

  renderProductInfo() {
    this.updateProductMediaGallery(this.getSelectedVariant());
  }

  updateProductMediaGallery(variant, isRetry = false) {
    if (!variant) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    if (!mediaGallery?.filterMediaByAlt) {
      if (!isRetry) {
        setTimeout(() => this.updateProductMediaGallery(variant, true), 150);
      }
      return;
    }

    const variantTerms = [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);

    mediaGallery.filterMediaByAlt(variantTerms);

    if (variant.featured_media?.id) {
      mediaGallery.setActiveMedia?.(`${this.dataset.section}-${variant.featured_media.id}`, true);
    }

    const openMode =
      window.EeConfiguratorGallery?.isOpenScrollMode?.() ?? window.scrollY > 200;
    window.EeConfiguratorGallery?.updateImageVisibility?.(openMode);
  }

  updateVariantInput(variantId) {
    let selectedVariant;
    
    if (variantId) {
      selectedVariant = this.getVariantData().find(variant => variant.id == variantId);
    } else {
      selectedVariant = this.getSelectedVariant();
    }
    
    if (!selectedVariant) return;

    this.currentVariant = selectedVariant;
    const variantIdStr = String(selectedVariant.id);
    this.updateURL(undefined, variantIdStr);

    const productForms = this.querySelectorAll('ee-product-form');
    productForms.forEach((formEl) => {
      const form = formEl.querySelector('form');
      if (form && window.EeConfiguratorVariantSync?.syncMainVariantInputs) {
        window.EeConfiguratorVariantSync.syncMainVariantInputs(form, variantIdStr);
      } else {
        const input = formEl.querySelector('input[name="id"]');
        if (input) input.value = variantIdStr;
      }
    });

    const addOns = document.querySelector('configurator-add-ons');
    if (addOns?.selectedAddOns?.size > 0 && typeof addOns.updateFormWithAddOns === 'function') {
      addOns.updateFormWithAddOns();
    }
  }

  updateSelectedVariant(optionValues) {
    // Function to handle complex variant selection
  }

  findVariantById(variantId, variants = this.getVariantData()) {
    if (!variantId || !variants?.length) return null;
    return variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  getSelectedVariantFromScript() {
    const script = this.querySelector('ee-variant-selects [data-selected-variant]');
    if (!script) return null;
    try {
      const variant = JSON.parse(script.textContent || '{}');
      return variant?.id ? variant : null;
    } catch (e) {
      console.error('Error parsing selected variant data', e);
      return null;
    }
  }

  getSelectedVariant() {
    const variants = this.getVariantData() || [];

    // 1. Product form id — set on load and kept in sync by card/swatch changes.
    //    Critical for sticky ATC pricing before any manual variant click.
    const form =
      this.querySelector('ee-product-form form') ||
      this.querySelector('form[action*="/cart/add"]') ||
      this.querySelector('form');
    const formVariantId =
      form?.querySelector('input[name="id"]')?.value ||
      form?.querySelector('input[name="items[0][id]"]')?.value;
    const fromForm = this.findVariantById(formVariantId, variants);
    if (fromForm) return fromForm;

    // 2. URL ?variant=
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    const fromUrl = this.findVariantById(urlVariantId, variants);
    if (fromUrl) return fromUrl;

    const variantSelects = this.querySelector('ee-variant-selects');
    if (!variantSelects) {
      return this.getSelectedVariantFromScript();
    }

    // 3. Match option values from configurator fieldsets.
    //    Cards use plain <fieldset>, not fieldset.product-form__input.
    const selectedOptions = [];
    variantSelects.querySelectorAll('select').forEach((select) => {
      if (select.value) selectedOptions.push(select.value);
    });
    variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio) selectedOptions.push(checkedRadio.value);
    });

    if (variants.length && selectedOptions.length) {
      const matched = variants.find((variant) =>
        variant.options.every((option, index) => selectedOptions[index] === option)
      );
      if (matched) return matched;
    }

    // 4. Checked configurator card data-variant-id
    const checkedCard = variantSelects.querySelector(
      'input[type="radio"][data-confg-variant-id]:checked, input[type="radio"][data-variant-id]:checked'
    );
    const fromCard = this.findVariantById(checkedCard?.dataset?.variantId, variants);
    if (fromCard) return fromCard;

    // 5. Liquid-rendered selected variant JSON
    const fromScript = this.getSelectedVariantFromScript();
    if (fromScript) {
      return this.findVariantById(fromScript.id, variants) || fromScript;
    }

    return null;
  }

  getVariantData() {
    // Get the full variant data for the product
    if (!this.variantData) {
      const productId = this.dataset.productId;
      const sectionId = this.dataset.section;

      let productJSON = null;

      const possibleIds = [
        `ProductJson-${productId}`,
        `ProductJSON-${productId}`,
        `ProductJson-${sectionId}`,
        `ProductJSON-${sectionId}`,
      ];

      for (const id of possibleIds) {
        const script = document.getElementById(id);
        if (script) {
          productJSON = script;
          break;
        }
      }

      // Prefer full variants list embedded in the variant picker
      if (!productJSON) {
        productJSON = this.querySelector('ee-variant-selects [data-product-variants]');
      }

      if (productJSON) {
        try {
          const parsed = JSON.parse(productJSON.textContent);
          this.variantData = Array.isArray(parsed)
            ? parsed
            : parsed?.variants || [];
        } catch (e) {
          console.error('Error parsing product JSON', e);
          this.variantData = [];
        }
      } else {
        const selected = this.getSelectedVariantFromScript();
        this.variantData = selected ? [selected] : [];
        if (!selected) {
          console.warn('No product JSON found');
        }
      }
    }

    return this.variantData;
  }

  dispatchVariantChangeEvent(variant) {
    if (!variant) return;

    this.currentVariant = variant;
    this.updateProductMediaGallery(variant);

    // Dispatch a custom event that can be listened for by other components
    this.dispatchEvent(new CustomEvent('configurator:variant-changed', {
      bubbles: true,
      detail: { variant }
    }));
  }
}

// Define our custom component
customElements.define('ee-configurator-product-info', EEConfiguratorProductInfo); 